import api from './api';
import { getDeviceFingerprint } from '@/utils/fingerprint';
import type {
  UserDevice,
  DeviceListResponse,
  TrustDeviceRequest,
  TrustDeviceResponse,
  SecurityAlert,
  SecurityAlertListResponse,
  AcknowledgeAlertResponse,
  AcknowledgeAllResponse,
} from '@/types';

// ============================================================================
// Backend response types (with camelCase from CamelCaseModel)
// ============================================================================

interface DeviceBackendResponse {
  id: string;
  deviceName: string;
  lastIp: string | null;
  lastLocation: string | null;
  isTrusted: boolean;
  trustedAt: string | null;
  trustExpiresAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  loginCount: number;
  isCurrent: boolean;
}

interface DeviceListBackendResponse {
  devices: DeviceBackendResponse[];
  total: number;
}

interface SecurityAlertBackendResponse {
  id: string;
  alertType: string;
  severity: string;
  details: Record<string, unknown> | null;
  deviceId: string | null;
  deviceName: string | null;
  emailSentAt: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

interface SecurityAlertListBackendResponse {
  alerts: SecurityAlertBackendResponse[];
  total: number;
  unacknowledgedCount: number;
}

// ============================================================================
// Transform functions
// ============================================================================

function transformDevice(d: DeviceBackendResponse): UserDevice {
  return {
    id: d.id,
    deviceName: d.deviceName,
    lastLocation: d.lastLocation,
    isTrusted: d.isTrusted,
    trustedAt: d.trustedAt,
    trustExpiresAt: d.trustExpiresAt,
    firstSeenAt: d.firstSeenAt,
    lastSeenAt: d.lastSeenAt,
    loginCount: d.loginCount,
    isCurrent: d.isCurrent,
  };
}

function transformAlert(a: SecurityAlertBackendResponse): SecurityAlert {
  return {
    id: a.id,
    alertType: a.alertType as SecurityAlert['alertType'],
    severity: a.severity as SecurityAlert['severity'],
    details: (a.details || {}) as SecurityAlert['details'],
    emailSentAt: a.emailSentAt,
    acknowledgedAt: a.acknowledgedAt,
    createdAt: a.createdAt,
  };
}

// ============================================================================
// Device Management API
// ============================================================================

export const securityApi = {
  // -------------------------------------------------------------------------
  // Devices
  // Note: Endpoints are at /devices (not /auth/devices)
  // -------------------------------------------------------------------------

  /**
   * Get list of user's devices
   */
  getDevices: async (): Promise<DeviceListResponse> => {
    const response = await api.get<DeviceListBackendResponse>('/devices');
    return {
      devices: response.devices.map(transformDevice),
      totalCount: response.total,
    };
  },

  /**
   * Get details of a specific device
   */
  getDevice: async (deviceId: string): Promise<UserDevice> => {
    const response = await api.get<DeviceBackendResponse>(`/devices/${deviceId}`);
    return transformDevice(response);
  },

  /**
   * Trust a device for a specified duration
   * Default duration is 30 days
   */
  trustDevice: async (
    deviceId: string,
    data?: TrustDeviceRequest
  ): Promise<TrustDeviceResponse> => {
    return api.post<TrustDeviceResponse>(`/devices/${deviceId}/trust`, {
      durationDays: data?.durationDays ?? 30,
    });
  },

  /**
   * Revoke trust from a device
   */
  revokeDeviceTrust: async (deviceId: string): Promise<{ message: string }> => {
    return api.post<{ message: string }>(`/devices/${deviceId}/revoke`);
  },

  /**
   * Delete/remove a device from the list
   */
  deleteDevice: async (deviceId: string): Promise<void> => {
    return api.delete(`/devices/${deviceId}`);
  },

  // -------------------------------------------------------------------------
  // Security Alerts
  // Note: Endpoints are at /security/alerts (not /auth/security/alerts)
  // -------------------------------------------------------------------------

  /**
   * Get list of recent security alerts
   */
  getAlerts: async (limit?: number): Promise<SecurityAlertListResponse> => {
    const params = limit ? { limit } : undefined;
    const response = await api.get<SecurityAlertListBackendResponse>('/security/alerts', params);
    return {
      alerts: response.alerts.map(transformAlert),
      unacknowledgedCount: response.unacknowledgedCount,
      totalCount: response.total,
    };
  },

  /**
   * Get details of a specific alert
   */
  getAlert: async (alertId: string): Promise<SecurityAlert> => {
    const response = await api.get<SecurityAlertBackendResponse>(`/security/alerts/${alertId}`);
    return transformAlert(response);
  },

  /**
   * Acknowledge (dismiss) a security alert
   */
  acknowledgeAlert: async (alertId: string): Promise<AcknowledgeAlertResponse> => {
    return api.post<AcknowledgeAlertResponse>(
      `/security/alerts/${alertId}/acknowledge`
    );
  },

  /**
   * Acknowledge all unacknowledged alerts
   */
  acknowledgeAllAlerts: async (): Promise<AcknowledgeAllResponse> => {
    return api.post<AcknowledgeAllResponse>('/security/alerts/acknowledge-all');
  },

  /**
   * Get the current device (the one making the request)
   * Includes device fingerprint in header for identification
   */
  getCurrentDevice: async (): Promise<UserDevice> => {
    const fingerprint = await getDeviceFingerprint();
    const response = await api.get<DeviceBackendResponse>(
      '/devices/current',
      undefined,
      { headers: { 'X-Device-Fingerprint': fingerprint } }
    );
    return transformDevice(response);
  },

  /**
   * Trust the current device for a specified duration
   * This is a convenience endpoint that trusts the device making the request
   * Includes device fingerprint in header for identification
   */
  trustCurrentDevice: async (durationDays: number = 30): Promise<TrustDeviceResponse> => {
    const fingerprint = await getDeviceFingerprint();
    return api.post<TrustDeviceResponse>(
      '/devices/current/trust',
      { durationDays },
      { headers: { 'X-Device-Fingerprint': fingerprint } }
    );
  },
};

export default securityApi;
